import React from "react";
import "./PrivacyPolicy.css";

function PrivacyPolicy() {
  const effectiveDate = "April 17, 2026";

  return (
    <div className="privacy-policy-page">
      <div className="privacy-policy-header">
        <button className="privacy-policy-back" onClick={() => (window.location.hash = "")}>
          ← Back
        </button>
        <h1>Privacy Policy</h1>
        <p>Last updated: {effectiveDate}</p>
      </div>

      <div className="privacy-policy-content">
        <h2>1. Introduction</h2>
        <p>
          Welcome to <strong>SalonTechPro</strong> ("we", "us", or "our"). We are committed to
          protecting your personal information and your right to privacy. This Privacy Policy
          explains how we collect, use, disclose, and safeguard your information when you use our
          salon management platform and related services, including our WhatsApp Business messaging
          integration.
        </p>
        <p>
          By using our services, you agree to the collection and use of information in accordance
          with this policy.
        </p>

        <h2>2. Information We Collect</h2>
        <h3>2.1 Personal Information</h3>
        <p>We may collect the following personal data when you interact with our platform:</p>
        <ul>
          <li>Full name (first name and last name)</li>
          <li>Mobile phone number</li>
          <li>Email address</li>
          <li>Physical address</li>
          <li>Appointment and service history</li>
          <li>Communication preferences (email, WhatsApp, SMS notification opt-in status)</li>
        </ul>

        <h3>2.2 Information Collected Automatically</h3>
        <ul>
          <li>Browser type and version</li>
          <li>IP address</li>
          <li>Session identifiers for authentication</li>
          <li>Usage data (pages visited, actions taken within the platform)</li>
        </ul>

        <h2>3. WhatsApp Messaging Integration</h2>
        <div className="privacy-whatsapp-badge">
          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
          </svg>
          WhatsApp Business API Integration
        </div>

        <h3>3.1 How We Use WhatsApp</h3>
        <p>
          Our platform integrates with the <strong>Meta WhatsApp Business Cloud API</strong> to send
          messages to clients. We use WhatsApp for the following purposes:
        </p>
        <ul>
          <li>
            <strong>Welcome messages:</strong> When a new client is added to our system and has
            opted in to WhatsApp notifications, we send a one-time welcome message using a
            pre-approved WhatsApp message template.
          </li>
          <li>
            <strong>Appointment reminders:</strong> Notifications about upcoming appointments or
            booking confirmations.
          </li>
          <li>
            <strong>Service-related updates:</strong> Important information related to services the
            client has engaged with.
          </li>
        </ul>

        <h3>3.2 WhatsApp Data Collection</h3>
        <p>When we communicate with you via WhatsApp, we process the following data:</p>
        <ul>
          <li>
            <strong>Phone number:</strong> Your mobile number in international format, used to
            deliver messages via WhatsApp.
          </li>
          <li>
            <strong>First name:</strong> Used to personalise template messages (e.g., welcome
            greetings).
          </li>
          <li>
            <strong>Message delivery status:</strong> We receive delivery and read receipts from the
            WhatsApp API.
          </li>
        </ul>

        <h3>3.3 Consent for WhatsApp Messaging</h3>
        <p>
          We only send WhatsApp messages to clients who have explicitly opted in to receive WhatsApp
          notifications. This consent is recorded in our system at the time of client registration or
          profile update. You can opt out at any time by:
        </p>
        <ul>
          <li>Requesting the salon staff to update your communication preferences.</li>
          <li>Contacting us directly using the details provided in the Contact section below.</li>
        </ul>

        <h3>3.4 Third-Party Data Sharing (WhatsApp / Meta)</h3>
        <p>
          To deliver WhatsApp messages, your phone number and message content are transmitted to{" "}
          <strong>Meta Platforms, Inc.</strong> via the WhatsApp Business Cloud API. Meta processes
          this data in accordance with the{" "}
          <a
            href="https://www.whatsapp.com/legal/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
          >
            WhatsApp Privacy Policy
          </a>{" "}
          and{" "}
          <a
            href="https://www.whatsapp.com/legal/business-policy"
            target="_blank"
            rel="noopener noreferrer"
          >
            WhatsApp Business Policy
          </a>
          . We do not sell or share your personal data with any other third parties for marketing
          purposes.
        </p>

        <h2>4. How We Use Your Information</h2>
        <p>We use the information we collect for the following purposes:</p>
        <ul>
          <li>To manage salon appointments, services, and client records.</li>
          <li>To communicate with you via your preferred channels (email, WhatsApp, SMS).</li>
          <li>To process sales and maintain transaction history.</li>
          <li>To manage inventory and staff scheduling.</li>
          <li>To authenticate your identity and secure your account.</li>
          <li>To generate business reports and analytics (aggregated, non-personal data).</li>
          <li>To comply with legal and regulatory obligations.</li>
        </ul>

        <h2>5. Data Storage and Security</h2>
        <p>We implement appropriate technical and organisational measures to protect your data:</p>
        <ul>
          <li>
            <strong>Encryption:</strong> Passwords are hashed using industry-standard cryptographic
            algorithms. All data in transit is encrypted via HTTPS/TLS.
          </li>
          <li>
            <strong>Access controls:</strong> Role-based access ensures only authorised salon staff
            can view client data. Administrative functions are restricted to admin users.
          </li>
          <li>
            <strong>Session management:</strong> Sessions are secured with authentication tokens and
            automatic expiration. Concurrent login restrictions can be enforced.
          </li>
          <li>
            <strong>Rate limiting:</strong> Login endpoints are protected against brute-force attacks.
          </li>
        </ul>

        <h2>6. Data Retention</h2>
        <p>
          We retain your personal data for as long as you remain an active client or as required by
          applicable law. If you request deletion of your data, we will process your request within
          30 days, subject to any legal retention requirements.
        </p>

        <h2>7. Your Rights</h2>
        <p>
          Depending on your jurisdiction, you may have the following rights regarding your personal
          data:
        </p>
        <ul>
          <li>
            <strong>Right to access:</strong> Request a copy of the personal data we hold about you.
          </li>
          <li>
            <strong>Right to rectification:</strong> Request correction of inaccurate or incomplete
            data.
          </li>
          <li>
            <strong>Right to erasure:</strong> Request deletion of your personal data.
          </li>
          <li>
            <strong>Right to restrict processing:</strong> Request that we limit how we use your
            data.
          </li>
          <li>
            <strong>Right to withdraw consent:</strong> Withdraw your consent for WhatsApp messaging
            or other communications at any time.
          </li>
          <li>
            <strong>Right to data portability:</strong> Request your data in a structured,
            machine-readable format.
          </li>
        </ul>
        <p>
          To exercise any of these rights, please contact us using the details in the Contact
          section below.
        </p>

        <h2>8. Cookies and Tracking</h2>
        <p>
          Our platform uses browser local storage to maintain your login session and preferences. We
          do not use third-party tracking cookies or advertising pixels.
        </p>

        <h2>9. Children's Privacy</h2>
        <p>
          Our services are not directed to individuals under the age of 16. We do not knowingly
          collect personal data from children. If you believe we have inadvertently collected data
          from a child, please contact us and we will promptly delete it.
        </p>

        <h2>10. Changes to This Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. Any changes will be posted on this
          page with an updated "Last updated" date. We encourage you to review this policy
          periodically.
        </p>

        <h2>11. Contact Us</h2>
        <div className="privacy-contact-box">
          <p>
            If you have any questions, concerns, or requests regarding this Privacy Policy or our
            data practices, please contact us:
          </p>
          <p>
            <strong>SalonTechPro</strong>
          </p>
          <p>
            Email:{" "}
            <a href="mailto:privacy@salontechpro.com">privacy@salontechpro.com</a>
          </p>
          <p>
            You may also speak with the salon management directly during your next visit.
          </p>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
